/**
 * Areas (slice 6d spec §11) and their query keys. `['area', slug]` is an Area
 * page, `['areas', …]` the lists; the `area` hub event invalidates both and
 * `/api/me` (the sidebar), and a task's, a thread's or a doc's event every
 * Area page (`app/events.ts`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../app/api";
import { meKey } from "../../app/me";
import type { ThreadItem } from "../chats/api";
import type { DocItem } from "../docs/api";
import type { RoutineItem } from "../routines/api";
import type { TaskItem } from "../tasks/api";

const areaKey = (slug: string) => ["area", slug] as const;
const areasKey = ["areas"] as const;

export type Area = {
  id: string;
  slug: string;
  label: string;
  icon: string;
  position: number;
  /** Where it stands (spec §3.2): '' until written. */
  summary: string;
  summary_by: string | null;
  summary_at: string | null;
  archived_at: string | null;
};

/** An Area as a row carries it (spec §4.4): its chip, greyed once archived. */
export type AreaRef = { id: string; slug: string; label: string; icon: string; archived: boolean };

/** An app's accounts the person can use, and the one the crew uses first in the Area (spec §9). */
export type AreaApp = {
  app: string;
  name: string;
  preferred: string | null;
  accounts: {
    id: string;
    label: string;
    email: string | null;
    status: string;
    shared_by: string | null;
  }[];
};

export type AreaPage = {
  area: Area;
  needs: TaskItem[];
  tasks: TaskItem[];
  task_counts: { open: number; done: number };
  chats: ThreadItem[];
  chat_count: number;
  docs: DocItem[];
  doc_count: number;
  routines: RoutineItem[];
  /** For this area (spec §9). Missing on an older Worker. */
  apps?: AreaApp[];
  /** The owner's (as Activity is); null for a member. */
  week: { replies: number; computer_ms: number } | null;
  /** What the crew knows (spec §8.2): its facts, for the card. */
  brief: {
    file_id: string;
    version: number;
    length: number;
    facts: string[];
    /** Each mentioned doc's title now, by id (spec §5.5). */
    titles: Record<string, string>;
  } | null;
};

export function useAreaPage(slug: string) {
  return useQuery({
    queryKey: areaKey(slug),
    queryFn: () => api<AreaPage>("GET", `/api/areas/${encodeURIComponent(slug)}`),
  });
}

/** Everything that shows Areas: the sidebar, the pages, the lists. */
function refreshAreas(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: meKey });
  void client.invalidateQueries({ queryKey: ["area"] });
  void client.invalidateQueries({ queryKey: areasKey });
}

/** `fact`: New area's "What should the crew know first?" (spec §8.4), its brief's first fact. */
type NewArea = { label: string; icon?: string; fact?: string };

/** An Area as `GET /api/areas` lists it. */
export type AreaListItem = Area & {
  counts: { open_tasks: number; chats: number; docs: number; routines: number };
};

/** The live Areas, in sidebar order (Memory's Areas section, spec §8.6). */
export function useAreas() {
  return useQuery({
    queryKey: areasKey,
    queryFn: () => api<{ areas: AreaListItem[] }>("GET", "/api/areas"),
  });
}

export function useCreateArea() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NewArea) => api<{ area: Area }>("POST", "/api/areas", input),
    onSuccess: () => refreshAreas(client),
  });
}

export type AreaEdit = {
  label?: string;
  icon?: string;
  summary?: string;
  position?: number;
  archived?: boolean;
};

export function useEditArea(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (edit: AreaEdit) => api<{ area: Area }>("PATCH", `/api/areas/${id}`, edit),
    onSuccess: () => refreshAreas(client),
  });
}

/** A chat Move these in? suggests (spec §7.2). */
type MoveInSuggestion = { id: string; title: string; last_message_at: string };
/** A doc it suggests (slice 6d PR 9). */
type MoveInDoc = { id: string; title: string; updated_at: string };
/** What Move these in? moves: the ticked chats and docs. */
type MoveInPick = { thread_ids: string[]; doc_ids: string[] };

/** Move these in?'s suggestions: read each time the window opens, Clef's or mock mode's. */
export function useMoveInSuggestions(areaId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["area-move-in", areaId],
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: () =>
      api<{ suggestions: MoveInSuggestion[]; docs: MoveInDoc[] }>(
        "GET",
        `/api/areas/${areaId}/suggestions`,
      ),
  });
}

/** Moves the ticked chats in, with their open tasks, and the ticked docs. */
export function useMoveIn(areaId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (pick: MoveInPick) =>
      api<{ moved: number; moved_docs: number }>("POST", `/api/areas/${areaId}/move-in`, pick),
    onSuccess: () => {
      refreshAreas(client);
      void client.invalidateQueries({ queryKey: ["area-move-in", areaId] });
      void client.invalidateQueries({ queryKey: ["threads"] });
      void client.invalidateQueries({ queryKey: ["thread"] });
      void client.invalidateQueries({ queryKey: ["tasks"] });
      void client.invalidateQueries({ queryKey: ["docs"] });
      void client.invalidateQueries({ queryKey: ["doc"] });
    },
  });
}

/** Use for this area (spec §9): the app's account the crew uses first in the Area, or none. */
export function usePreferAccount(areaId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ app, accountId }: { app: string; accountId: string | null }) =>
      api<null>("PUT", `/api/areas/${areaId}/accounts/${app}`, { account_id: accountId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["area"] }),
  });
}
